import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Play, X, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Vimeo: { type: "vimeo", id, h? }
// YouTube: { type: "youtube", id, list? }
const PROJECTS = [
  // ---------- CORPORATE ----------
  {
    id: "brand-film-corp",
    title: "Brand Film",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Brand Film",
    year: "2025",
    description:
      "Our flagship corporate brand film — voice, vision and visuals in one cinematic piece.",
    video: { type: "vimeo", id: "1085801312" },
    featured: true,
  },
  {
    id: "product-promo",
    title: "Product Promo",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Product",
    year: "2025",
    description:
      "Short-form product promo built for paid social and web hero placements.",
    video: { type: "vimeo", id: "1138785853", h: "1705ebcb78" },
  },
  {
    id: "product-videos",
    title: "Product Videos",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Product",
    year: "2024",
    description:
      "Studio-style product features designed to convert on landing pages.",
    video: { type: "vimeo", id: "1008467205", h: "abaf382394" },
  },
  {
    id: "recruitment",
    title: "Recruitment Video",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Recruitment",
    year: "2022",
    description:
      "Recruitment film that shows the culture candidates actually want to join.",
    video: { type: "vimeo", id: "657065826" },
  },
  {
    id: "hr-recruitment",
    title: "HR Recruitment Film",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Recruitment",
    year: "2024",
    description:
      "A polished HR recruitment piece — humans-first storytelling for hiring.",
    video: { type: "vimeo", id: "1024844049", h: "0d73c882bd" },
  },
  {
    id: "product-video-2",
    title: "Product Spotlight",
    client: "Corporate Client",
    category: "Corporate",
    tag: "Product",
    year: "2025",
    description:
      "Another product film built to showcase features in the best possible light.",
    video: { type: "vimeo", id: "1193815819" },
  },

  // ---------- COURSE / EDUCATION ----------
  {
    id: "newlyfed",
    title: "Newlyfed — Cooking Show / Course",
    client: "Kosher.com",
    category: "Course",
    tag: "Cooking Show",
    year: "2024",
    description:
      "A full-series cooking show & course produced for Kosher.com — set design, multi-cam capture, motion graphics and post.",
    external: "https://www.kosher.com/shows/newlyfed/",
    externalLabel: "Visit Show",
  },
  {
    id: "wurkfit",
    title: "Wurkfit — Online Course Series",
    client: "Wurkfit",
    category: "Course",
    tag: "Fitness Course",
    year: "2024",
    description:
      "A library of online fitness courses produced end-to-end — curriculum filming, graphics and lesson edits.",
    external: "https://www.wurkfit.com/onlinecourses",
    externalLabel: "Visit Courses",
  },
  {
    id: "course-segment",
    title: "Course Segment Preview",
    client: "Insight Media",
    category: "Course",
    tag: "Course",
    year: "2025",
    description:
      "A short segment from one of our recent video courses — a taste of the production quality we bring to educational series.",
    video: { type: "vimeo", id: "1193817902", h: "442fd5f1ae" },
  },

  // ---------- NONPROFIT ----------
  {
    id: "fun-brand",
    title: "Out of the Box — Branded Story Film",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Branded",
    year: "2025",
    description:
      "An unconventional branded story film built to break through the noise.",
    video: { type: "vimeo", id: "1163416887" },
  },
  {
    id: "brand-film-np",
    title: "Classic Brand Film",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Brand Film",
    year: "2025",
    description:
      "Cinematic nonprofit brand storytelling — mission, voice and visuals.",
    video: { type: "vimeo", id: "1131140866" },
  },
  {
    id: "fundraising",
    title: "Classic Fundraising Video",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Fundraising",
    year: "2024",
    description:
      "A donor-focused appeal designed to convert generosity into action.",
    video: { type: "vimeo", id: "1124675647", h: "85c4368be8" },
  },
  {
    id: "event-reel-1",
    title: "Event Reel — Show-At-Event Film",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Event",
    year: "2024",
    description:
      "A high-impact film built to play live at a gala or major event.",
    video: { type: "vimeo", id: "1034033971" },
  },
  {
    id: "event-reel-2",
    title: "Event Reel — Gala Showcase",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Event",
    year: "2024",
    description:
      "Another gala-ready showcase film produced end-to-end in-house.",
    video: { type: "vimeo", id: "963074831" },
  },
  {
    id: "gala-3",
    title: "Gala Video",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Gala",
    year: "2024",
    description:
      "A high-energy gala film built to lift the room and the pledge cards.",
    video: { type: "vimeo", id: "1041840074" },
  },
  {
    id: "event-highlight-1",
    title: "Event Highlight Film",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Event",
    year: "2025",
    description:
      "A fast-cut event highlight piece — recap reel built for social and email.",
    video: { type: "vimeo", id: "1166435960", h: "258a24b1cf" },
  },
  {
    id: "event-highlight-2",
    title: "Event Highlight — Encore",
    client: "Nonprofit Client",
    category: "Nonprofit",
    tag: "Event",
    year: "2024",
    description:
      "Another event highlight reel — story, energy, sponsor recognition.",
    video: { type: "vimeo", id: "1049082832", h: "eeda1f5581" },
  },

  // ---------- PODCAST ----------
  {
    id: "podcast-jewish-grit",
    title: "Jewish Grit Podcast",
    client: "Jewish Grit",
    category: "Podcast",
    tag: "Podcast",
    year: "2024",
    description:
      "Multi-cam video podcast — produced, edited and distributed across platforms.",
    video: {
      type: "youtube",
      id: "GF3c4ANSKcE",
      list: "PLDMcy6bumVWYLCKZLzVn7k7gwLb2vWUhl",
    },
  },
  {
    id: "podcast-second-opinion",
    title: "The Second Opinion Podcast",
    client: "The Second Opinion",
    category: "Podcast",
    tag: "Podcast",
    year: "2024",
    description:
      "A long-running video podcast produced and distributed end-to-end across YouTube.",
    external: "https://www.youtube.com/@TheSecondOpinionPodcast-t5w",
    externalLabel: "View Channel",
  },
  {
    id: "womens-market-live",
    title: "The Women's Market — Live Shopping Series",
    client: "The Women's Market",
    category: "Podcast",
    tag: "Live Shopping",
    year: "2026",
    description:
      "A weekly live shopping series — one curated brand per episode, filmed in-studio with founder storytelling and on-camera product demos.",
    video: { type: "vimeo", id: "1221239550", h: "07d4f142e2" },
    external: "https://womensmarketlive.com",
    externalLabel: "Visit Site",
  },
];

const CATEGORIES = ["All", "Corporate", "Nonprofit", "Course", "Podcast"];

function buildEmbed(v) {
  if (!v) return "";
  if (v.type === "vimeo") {
    const hash = v.h ? `h=${v.h}&` : "";
    return `https://player.vimeo.com/video/${v.id}?${hash}autoplay=1&title=0&byline=0&portrait=0`;
  }
  const list = v.list ? `&list=${v.list}` : "";
  return `https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0${list}`;
}

function initialThumb(v) {
  if (!v) return "";
  if (v.type === "youtube") {
    return `https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`;
  }
  // Vimeo: start with vumbnail; backend will override with the official oEmbed thumb
  return `https://vumbnail.com/${v.id}.jpg`;
}

function Card({ p, thumb, onPlay, large = false }) {
  const [imgFailed, setImgFailed] = useState(false);
  const isExternalOnly = !p.video && !!p.external;

  const handleClick = (e) => {
    if (isExternalOnly) {
      // Let the wrapping anchor handle it; this is a button so we open programmatically.
      e.preventDefault();
      window.open(p.external, "_blank", "noopener,noreferrer");
    } else {
      onPlay(p);
    }
  };

  return (
    <div
      className={`group relative bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#E60000] transition-colors duration-500 reveal in-view ${
        large ? "md:col-span-2" : ""
      }`}
      data-testid={`work-card-${p.id}`}
    >
      <button
        type="button"
        onClick={handleClick}
        data-testid={`work-play-${p.id}`}
        className="block w-full text-left"
        aria-label={isExternalOnly ? `Visit ${p.title}` : `Play ${p.title}`}
      >
        <div className="relative overflow-hidden aspect-[16/9] bg-black">
          {!imgFailed && thumb ? (
            <img
              src={thumb}
              alt={p.title}
              loading="lazy"
              onError={(e) => {
                if (e.currentTarget.src.includes("maxresdefault")) {
                  e.currentTarget.src = e.currentTarget.src.replace(
                    "maxresdefault",
                    "hqdefault"
                  );
                } else {
                  setImgFailed(true);
                }
              }}
              className="h-full w-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
            />
          ) : (
            // Cinematic fallback when no thumbnail loads
            <div className="h-full w-full bg-gradient-to-br from-[#1a0000] via-[#0a0a0a] to-[#000] flex items-center justify-center">
              <p className="font-display uppercase text-3xl md:text-5xl text-outline-red leading-none px-8 text-center">
                {p.tag}
              </p>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

          <span className="absolute inset-0 flex items-center justify-center">
            <span className="relative inline-flex h-16 w-16 md:h-20 md:w-20 items-center justify-center bg-[#E60000] text-white group-hover:bg-white group-hover:text-[#E60000] transition-colors duration-300">
              {isExternalOnly ? (
                <ExternalLink size={22} strokeWidth={2.4} />
              ) : (
                <Play size={22} fill="currentColor" />
              )}
            </span>
          </span>

          <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
            <span className="eyebrow bg-[#E60000] text-white px-2.5 py-1.5">
              {p.tag}
            </span>
            <span className="eyebrow text-white/80">{p.year}</span>
          </div>
        </div>

        <div className="flex items-start justify-between gap-6 p-6 md:p-8">
          <div>
            <p className="eyebrow text-white/40 mb-3">{p.client}</p>
            <h3 className="font-display-md uppercase text-xl md:text-2xl leading-tight max-w-md">
              {p.title}
            </h3>
            {p.description && (
              <p className="text-white/60 mt-3 text-sm md:text-base leading-relaxed max-w-md">
                {p.description}
              </p>
            )}
          </div>
          <span className="eyebrow text-white/40 group-hover:text-[#E60000] transition-colors whitespace-nowrap">
            {isExternalOnly ? "Visit →" : "Play →"}
          </span>
        </div>
      </button>

      {p.external && !isExternalOnly && (
        <a
          href={p.external}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`work-external-${p.id}`}
          className="absolute bottom-6 right-6 md:bottom-8 md:right-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-white/60 hover:text-[#E60000] transition-colors"
        >
          {p.externalLabel || "Open"} <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
}

function Lightbox({ project, onClose }) {
  useEffect(() => {
    if (!project) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, onClose]);

  if (!project) return null;

  return (
    <div
      data-testid="video-lightbox"
      className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={project.title}
    >
      <button
        onClick={onClose}
        data-testid="video-lightbox-close"
        aria-label="Close video"
        className="absolute top-5 right-5 md:top-8 md:right-8 inline-flex h-12 w-12 items-center justify-center border border-[#333] text-white hover:bg-[#E60000] hover:border-[#E60000] transition-colors z-10"
      >
        <X size={20} />
      </button>

      <div className="w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video w-full bg-black border border-[#1a1a1a]">
          <iframe
            src={buildEmbed(project.video)}
            title={project.title}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
          />
        </div>
        <div className="mt-5 flex items-center justify-between text-white/70">
          <div>
            <p className="eyebrow text-[#E60000] mb-2">
              {project.category} · {project.tag}
            </p>
            <h4 className="font-display-md uppercase text-lg md:text-xl">
              {project.title}
            </h4>
          </div>
          <p className="eyebrow text-white/40">{project.year}</p>
        </div>
      </div>
    </div>
  );
}

export default function Work() {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");
  const [thumbs, setThumbs] = useState(() => {
    const m = {};
    PROJECTS.forEach((p) => (m[p.id] = initialThumb(p.video)));
    return m;
  });

  // Fetch official Vimeo thumbnails (works for unlisted videos w/ hash).
  // Update each thumb as it resolves so the UI fills in progressively.
  useEffect(() => {
    let cancelled = false;
    PROJECTS.filter((p) => p.video?.type === "vimeo").forEach(async (p) => {
      try {
        const params = { id: p.video.id };
        if (p.video.h) params.h = p.video.h;
        const r = await axios.get(`${API}/vimeo-thumb`, { params });
        const url = r.data?.thumbnail_url;
        if (!cancelled && url) {
          setThumbs((t) => ({ ...t, [p.id]: url }));
        }
      } catch {
        /* keep fallback */
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (filter === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.category === filter);
  }, [filter]);

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-16 border-t border-[#1a1a1a]"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16 reveal">
        <div>
          <p className="eyebrow text-[#E60000] mb-5">/ Featured Work</p>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">
            Recent
            <br />
            <span className="text-outline">productions.</span>
          </h2>
        </div>
        <div className="md:max-w-md">
          <p className="text-white/70 leading-relaxed md:text-lg mb-4">
            A selection of recent films for corporations, nonprofits and
            podcasters. Click any thumbnail to watch.
          </p>
          <a
            href="#contact"
            data-testid="work-cta"
            className="eyebrow text-[#E60000] hover:text-white transition-colors inline-flex items-center gap-2"
          >
            Discuss your project →
          </a>
        </div>
      </div>

      {/* Filter tabs */}
      <div
        className="flex flex-wrap items-center gap-2 mb-10 md:mb-14 border-b border-[#1a1a1a] pb-6"
        data-testid="work-filters"
        role="tablist"
      >
        {CATEGORIES.map((c) => {
          const count =
            c === "All"
              ? PROJECTS.length
              : PROJECTS.filter((p) => p.category === c).length;
          const isActive = filter === c;
          return (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase()}`}
              className={`inline-flex items-center gap-3 px-4 md:px-5 py-3 border text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
                isActive
                  ? "bg-[#E60000] border-[#E60000] text-white"
                  : "bg-transparent border-[#222] text-white/70 hover:border-[#E60000] hover:text-white"
              }`}
            >
              <span>{c}</span>
              <span
                className={`text-[10px] ${
                  isActive ? "text-white/80" : "text-white/40"
                }`}
              >
                {String(count).padStart(2, "0")}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {visible.map((p) => (
          <Card
            key={p.id}
            p={p}
            thumb={thumbs[p.id]}
            onPlay={setActive}
            large={p.featured}
          />
        ))}
      </div>

      <Lightbox project={active} onClose={() => setActive(null)} />
    </section>
  );
}
